// VariantParams — Set7 (2026-08-25): the per-recipe-IDENTITY variant params TEXTURE.  Set6 moved
// param VALUES into a per-identity `UniformsGroup` (UBO), which is fundamentally capped by the
// WebGL2 binding-point ceiling (MAX_UNIFORM_BUFFER_BINDINGS spec floor 24 — ~2 points per variant
// → ~a dozen variants on an Android spec-floor device, vs the requirement of >2 dozen).  Set7
// moves those same per-identity VALUES into a single RGBA32F DataTexture (one ROW per identity
// variant; ONE shared texture for all variants — O(1) in variant count), indexed per chunk by a
// plain `int` uniform `u_variantRow`.  This file is the structural template for the packing
// side; it mirrors `RegionParams.ts` (the per-REGION params texture this generalises).
//
// Column layout (RGBA32F — plan §2.4.1): a ROW is a row of RGBA texels.  A param occupies a run
// of FLOAT columns, where each texel = 4 float columns (r, g, b, a):
//   - scalar → 1 float (texel .r)
//   - color  → 3 floats (texel .rgb)
//   - ramp   → RAMP_MAX_STOPS(6) × 4 floats per stop = 24 floats = 6 texels (1 vec4 stop per
//              texel, the plan §2.4 "6 fetches per ramp" win — a single texelFetch yields a
//              whole vec4 stop).
// Unlike the std140 UBO layout (`PackedParamLayout.offsetFloats`), a texture has NO alignment
// rules: `color` packs at 3 floats instead of 4, and params follow each other contiguously.
// Ramps are aligned to a TEXEL boundary (start at a multiple of 4 floats) so a `variantRamp()`
// fetch address is a whole texel; scalar/color texturing never straddles a texel, so their
// channel-based reads are unambiguous.
//
// The column assignment MUST match the assembler's (`ShaderAssembler.assembleMaterialShader`
// builds `variantParamColumns` in the SAME order: `collectUsedParams` order — the set-wide
// used-param order — with the SAME cursor walk).  This parity is asserted by a dedicated test
// (`VariantParams.test.ts`), not just comment-enforced as the RegionParams ↔ assembler contract
// historically was.  `packVariantParamsRow` reuses `UniformPacker.resolveScalar` as the
// validated-default choke point (§11.5; the shared sampler for the UBO path's param membership).

import {RAMP_MAX_STOPS, type ParamKind, type ParamValue} from "../@MaterialTypes.js";
import type {ResolvedParam} from "../RecipeResolverCore.js";

/** Column layout for one used param in a variant-params row (mirror of the assembler's cursor
 *  map — see the parity test `VariantParams.test.ts`).  Keyed like the UBO by the SET-WIDE
 *  global key `${recipeID}/${paramKey}` (used params are already deduped set-wide). */
export interface VariantParamColumnLayout {
	/** Set-wide global param key `${recipeID}/${paramKey}` (the same key shape UsedParams uses). */
	paramKey: string;
	kind: ParamKind;
	/** First FLOAT column of this param in the row (row of RGBA texels). */
	column: number;
	/** Float span: scalar = 1, color = 3, ramp = RAMP_MAX_STOPS * 4 (24). */
	span: number;
	/** The TEXEL containing `column` (floor(column / 4)); for a ramp this is the first stop's texel. */
	texel: number;
	/** The channel within that texel at which the param starts (column % 4); for a ramp this is 0. */
	channel: number;
	/** The param's schema-typed RESOLVED default (raw value; used when the row leaves it unset).
	 *  Carries THIS identity set's value (used params are per identity). */
	defaultValue: ParamValue;
}

/** Float span per kind in the variant-row layout (no std140 padding — color = 3, not 4; ramp
 *  = 24 = 6 texels).  Ramps are force-aligned to a texel boundary (multiples of 4 floats). */
export function variantParamSpan(kind: ParamKind): number {
	if (kind == "scalar") return 1;
	if (kind == "color") return 3;
	return RAMP_MAX_STOPS * 4; // 24 = 6 vec4 stop-texels
}

/** Build the variant-params column layout for a set of USED params (already deduped, declaration
 *  order — exactly `collectUsedParams`' output, the SAME order the assembler walks).  MUST
 *  mirror the assembler's inline cursor in `assembleMaterialShader` — a drift here breaks every
 *  row's values (the assembly parity test catches it).  `totalColumns` (optional) lets the caller
 *  pre-declare the texture width and bounds-check against it. */
export function buildVariantParamLayouts(usedParams: ResolvedParam[], totalColumns?: number): VariantParamColumnLayout[] {
	const layouts: VariantParamColumnLayout[] = [];
	let col = 0;
	for (const p of usedParams) {
		const span = variantParamSpan(p.kind);
		// Ramps + COLOURS: force-aligned to a TEXEL boundary (float-col multiple of 4).  A colour
		// must never straddle two texels (a texelFetch returns one texel's vec4 — a straddling
		// colour would need two fetches); aligning it to channel 0 keeps its read `.rgb` of one
		// texel.  Ramps align so the 6-stop fetch addresses whole texels.  Scalars pack into the
		// remaining channels of the current texel (no waste).
		if (p.kind == "ramp" || p.kind == "color") col = Math.ceil(col / 4) * 4;
		if (totalColumns != null && col + span > totalColumns) {
			throw new Error(`[VariantParams] params exceed the variant-row column budget (${totalColumns})`);
		}
		layouts.push({
			paramKey: p.key,
			kind: p.kind,
			column: col,
			span,
			texel: Math.floor(col / 4),
			channel: col % 4,
			defaultValue: p.default,
		});
		col += span;
	}
	return layouts;
}

/** Pack ONE variant row (a recipe-identity set's resolved defaults) into a Float32Array laid out
 *  as a run of RGBA texels: `row[col]` = the param's float at the layout's `column` (row-major
 *  over RGBA texels, so texel t occupies row[4t .. 4t+3]).  `values` keyed by the set-wide global
 *  key `${recipeID}/${paramKey}` (the same key shape packMaterialParams uses); absent keys fall
 *  back to the layout's resolved default (each layout's `defaultValue` carries ITS identity's
 *  resolved default — see `resolvedParamsToRow`).  Ramps write 6 vec4 stops (padded with the
 *  LAST stop at t = 1.0, mirroring the UBO packer so `rampVal` terminates identically).
 *  Returns a Float32Array of `columns` floats (the texture's width × 4 — the ROW of texels). */
export function packVariantParamsRow(
	layouts: VariantParamColumnLayout[],
	values: {[globalKey: string]: ParamValue},
	columns: number,
): Float32Array {
	if (columns < 1) throw new Error("[VariantParams] need at least one column (texel width)");
	const out = new Float32Array(columns);
	for (const l of layouts) {
		const v = values[l.paramKey] ?? l.defaultValue;
		const base = l.column;
		switch (l.kind) {
			case "scalar": {
				out[base] = typeof v == "number" && isFinite(v) ? v : (typeof l.defaultValue == "number" ? l.defaultValue : 0);
				break;
			}
			case "color": {
				const c = Array.isArray(v) ? v : (Array.isArray(l.defaultValue) ? l.defaultValue : [0, 0, 0]);
				for (let i = 0; i < 3; i++) out[base + i] = typeof c[i] == "number" ? c[i] : 0;
				break;
			}
			case "ramp": {
				const stops = Array.isArray(v) ? v : (l.defaultValue as number[]);
				if (stops.length % 4 != 0) throw new Error(`[VariantParams] ramp param "${l.paramKey}" must be t,r,g,b quads (got length ${stops.length})`);
				if (stops.length > RAMP_MAX_STOPS * 4) throw new Error(`[VariantParams] ramp param "${l.paramKey}" exceeds ${RAMP_MAX_STOPS} stops`);
				for (let i = 0; i < stops.length; i++) out[base + i] = typeof stops[i] == "number" ? stops[i] : 0;
				// Pad trailing stop-texels with the LAST stop's colour at t = 1.0 (mirror of the
				// literal-ramp + UBO-packer paths) so `rampVal` terminates identically.
				if (stops.length > 0) {
					const lastT = stops[stops.length - 4];
					const lastRgb = [stops[stops.length - 3], stops[stops.length - 2], stops[stops.length - 1]];
					for (let i = stops.length; i < RAMP_MAX_STOPS * 4; i += 4) {
						out[base + i + 0] = Math.max(lastT, 1.0);
						out[base + i + 1] = lastRgb[0];
						out[base + i + 2] = lastRgb[1];
						out[base + i + 3] = lastRgb[2];
					}
				}
				break;
			}
			case "int":
			case "enum":
			case "bool":
			case "vec3": {
				// int/enum/bool/vec3 params are STRUCTURAL — they never reach the used-param set
				// with a runtime-writable scalar (the assembler bakes them as literals).  A
				// defensive single-float fallback keeps the packer total (should never fire).
				out[base] = typeof v == "number" ? v : 0;
				break;
			}
		}
	}
	return out;
}

/** Pack a variant row straight from a set of layouts built via
 *  `buildVariantParamLayouts(collectUsedParams(recipes), ...)`.  Each layout's `defaultValue`
 *  already carries THE IDENTITY SET's resolved default (collectUsedParams wires each used param
 *  to its owning recipe's resolved param), so packing with an empty override map yields the
 *  identity's values — the exact per-identity row the manager stores.  `columns` = the texture's
 *  float width (texels × 4). */
export function packIdentityRow(layouts: VariantParamColumnLayout[], columns: number): Float32Array {
	return packVariantParamsRow(layouts, {}, columns);
}
